import { type Component, type Editor } from 'grapesjs';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  Button,
  FormBase,
  ListLoading,
  Logger,
  SelectData,
  useToast,
} from '@gbs-monorepo-packages/common';
import { IFormPopoverProps } from '@gbs-monorepo/edge/components/FormPopover';
import {
  AutobuildTagTypes,
  IAutobuildTagType,
} from '@gbs-monorepo/edge/constants/AutobuildTagTypes';
import { getAutobuildMapping } from '@gbs-monorepo/edge/services/courses';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  AutobuildSchema,
  autobuildSchema,
} from '../../../../CustomText/ModalAutobuildSettings/autobuildSchema';
import { ContentButton } from './style';

interface IDataItems {
  key: string;
  value: string;
}

interface IModalUploadImage
  extends Partial<Omit<IFormPopoverProps, 'children'>> {
  editor: Editor | null;
  open: boolean;
  isSelectDropDownOpen: MutableRefObject<Set<unknown>>;
  onClose: () => void;
}

export const FormAutoBuildLink = ({
  editor,
  open,
  onClose,
  isSelectDropDownOpen,
}: IModalUploadImage): JSX.Element => {
  const [types, setTypes] = useState<IAutobuildTagType[]>();
  const [properties, setProperties] = useState<IDataItems[] | null>(null);
  const [autobuildMapping, setAutobuildMapping] =
    useState<Record<string, string>>();
  const [property, setProperty] = useState<string | null>(null);
  const [lockType, setLockType] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { addToast } = useToast();

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectDropDownOpen.current.add(key);
    } else {
      isSelectDropDownOpen.current.delete(key);
    }
  }, []);

  const autobuild = useForm<AutobuildSchema>({
    resolver: zodResolver(autobuildSchema),
    defaultValues: {
      type: '',
      property: '',
      order: 0,
    },
  });

  const {
    reset,
    formState: { errors },
    setValue,
    setError,
    watch,
    handleSubmit,
  } = autobuild;

  const selectDataType = useRef(null);
  const selectDataProperty = useRef(null);

  const values = watch();

  const handleTypeSelectChange = (value: string) => {
    setValue('type', value);

    if (autobuildMapping !== undefined && value in autobuildMapping) {
      const subData = autobuildMapping[value];
      const propertiesAux: IDataItems[] = [];

      Object.keys(subData).forEach((key: string) => {
        propertiesAux.push({ key, value: String(key) });
      });

      if (propertiesAux.length === 0) {
        setError('property', {
          type: 'manual',
          message: 'No properties found for this type',
        });
        setProperties(null);
      } else {
        setProperties(propertiesAux);
        setError('property', {
          type: 'manual',
          message: '',
        });
      }
    } else {
      setProperties([]);
    }

    setValue('property', '');
  };

  const handlePropertySelectChange = (value: string) => {
    setValue('property', value);
  };

  const resetForm = useCallback(() => {
    onClose?.();
    setProperty(null);
    reset();
  }, [onClose, reset]);

  const handleDecline = useCallback(() => {
    if (!isSelectDropDownOpen.current.size) {
      resetForm();
    }
  }, [resetForm]);

  const onSubmit: SubmitHandler<AutobuildSchema> = (data: AutobuildSchema) => {
    const component = editor?.getSelected();

    if (component !== undefined) {
      component?.setAttributes({
        'data-gjs-autobuild-type': data.type,
        'data-gjs-autobuild-marker': data.property,
        'data-gjs-autobuild-order': data.order,
        'data-gjs-autobuild-lock-type': lockType ? 'true' : 'false',
      });
    } else {
      addToast({
        title: 'Failed to change autobuild settings',
        styleType: 'error',
        dataCy: 'autobuild-settings-error-toast',
        duration: 5000,
      });
    }

    onClose?.();
  };

  useEffect(() => {
    const syncAutobuildMapping = (component: Component) => {
      setIsLoading(true);
      getAutobuildMapping()
        .then((mapping) => {
          const typeAux: IAutobuildTagType[] = [];
          typeAux.push({
            key: AutobuildTagTypes.Files,
            value: AutobuildTagTypes.Files,
          });
          if (typeAux.length > 0) {
            setTypes(typeAux);
            handleTypeSelectChange(typeAux[0].key);
          }
          setAutobuildMapping(mapping.data);

          if (component !== undefined) {
            updatedForm(component, mapping.data);
          }
        })
        .catch((error) => {
          Logger.debug('error: ', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    const component = editor?.getSelected();
    if (open && component && component.get('type') === 'document-link') {
      syncAutobuildMapping(component);
    }

    return () => {
      setIsLoading(false);
    };
  }, [editor?.getSelected(), setValue, open]);

  const updatedForm = useCallback(
    (component: Component, mapping?: Record<string, string>) => {
      const type =
        (component.getAttributes()['data-gjs-autobuild-type'] as string) ||
        null;
      const order =
        (component.getAttributes()['data-gjs-autobuild-order'] as string) ||
        null;
      const propertyAux =
        (component.getAttributes()['data-gjs-autobuild-marker'] as string) ||
        null;
      const lockTypeAux =
        (component.getAttributes()['data-gjs-autobuild-lock-type'] as string) ||
        null;

      setValue('type', type !== null ? type : '');
      setValue('order', order !== null ? Number(order) : 0);
      setValue('property', propertyAux !== null ? propertyAux : '');
      setLockType(lockTypeAux !== null ? lockTypeAux === 'true' : false);

      const currentMapping = mapping || autobuildMapping;

      if (type !== null && currentMapping !== undefined) {
        const subData = currentMapping[type];
        if (subData === undefined) return;
        const propertiesAux: IDataItems[] = [];

        Object.keys(subData).forEach((key: string) => {
          propertiesAux.push({ key, value: String(key) });
        });

        setProperties(propertiesAux);
        setProperty(propertyAux);
      } else {
        setProperty('');
      }
    },
    [autobuildMapping, setValue]
  );

  useEffect(() => {
    if (!open) {
      setValue('property', property !== null ? property : '');
    }
  }, [property, open]);

  if (isLoading) {
    return <ListLoading />;
  }
  return (
    <FormBase.Provider {...autobuild}>
      <FormBase.Root data-cy="formMailTo" onSubmit={handleSubmit(onSubmit)}>
        <FormBase.Content>
          <FormBase.InputContent
            filled={!!values.type}
            inputRef="select-type"
            label="Type"
            errorMessage={errors.type?.message}
            dataCy="autobuild-type"
            value={values.type.length > 0 ? 'type' : ''}
            isSelectData
          >
            {types !== undefined && (
              <SelectData
                data={types}
                value={values.type}
                dataCy={values.type}
                onValueChange={handleTypeSelectChange}
                zIndex={10}
                ref={selectDataType}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'type');
                }}
                disabled={lockType}
              />
            )}
          </FormBase.InputContent>

          <FormBase.InputContent
            filled={!!values.property}
            inputRef="select-property"
            label="Property"
            errorMessage={errors.property?.message}
            dataCy="autobuild-property"
            value={values.property.length > 0 ? 'property' : ''}
            isSelectData
          >
            <SelectData
              data={properties === null ? [] : properties}
              value={values.property}
              dataCy={values.property}
              onValueChange={handlePropertySelectChange}
              zIndex={10}
              disabled={!values.type || properties === null}
              ref={selectDataProperty}
              onOpenChange={(isOpen) => {
                handleOpenChange(isOpen, 'property');
              }}
            />
          </FormBase.InputContent>

          <FormBase.InputContent
            filled={!!values?.order}
            inputRef="order"
            label="Order"
            errorMessage={errors.order?.message}
            dataCy="autobuild-order"
          >
            <FormBase.InputText
              autoComplete="off"
              dataCy="input-order"
              id="order"
              type="number"
              name="order"
              min={1}
            />
          </FormBase.InputContent>

          <ContentButton>
            <Button type="button" styleType="outline" onClick={handleDecline}>
              Cancel
            </Button>
            <Button type="submit">Confirm</Button>
          </ContentButton>
        </FormBase.Content>
      </FormBase.Root>
    </FormBase.Provider>
  );
};
